import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Plus, DownloadSimple, Trash, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { SectionHeader, EmptyState, StatusBadge, fmtDate } from "./ui";

const CATEGORIES = ["Identification", "Credit", "Financial", "Contracts", "Agreements", "Taxes", "Corporate", "Banking", "Insurance", "Licensing", "Other"];
const EMPTY = { name: "", category: "Other", client_id: "none", company_id: "none", expiration_date: "", client_visible: false };

export default function Documents() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [sp, setSp] = useSearchParams();

  const load = useCallback(async () => {
    const [d, cl, co] = await Promise.all([api.get("/cc/documents"), api.get("/cc/clients"), api.get("/cc/companies")]);
    setRows(d.data); setClients(cl.data); setCompanies(co.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (sp.get("new") === "1") { setForm(EMPTY); setFile(null); setOpen(true); sp.delete("new"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!file) { toast.error("Choose a file to upload"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", form.name || file.name);
      fd.append("category", form.category);
      if (form.client_id !== "none") fd.append("client_id", form.client_id);
      if (form.company_id !== "none") fd.append("company_id", form.company_id);
      if (form.expiration_date) fd.append("expiration_date", form.expiration_date);
      fd.append("client_visible", form.client_visible);
      await api.post("/cc/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setOpen(false); setForm(EMPTY); setFile(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setSaving(false); }
  };

  const view = async (doc) => {
    try {
      const res = await api.get(`/cc/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try { await api.delete(`/cc/documents/${doc.id}`); setRows((r) => r.filter((x) => x.id !== doc.id)); toast.success("Deleted"); }
    catch { toast.error("Failed to delete"); }
  };

  const openEditDoc = (d) => {
    setEditRow(d.id);
    setEditForm({ name: d.name || "", category: d.category || "Other", client_id: d.client_id || "none", company_id: d.company_id || "none", expiration_date: d.expiration_date || "", client_visible: !!d.client_visible });
  };
  const setE = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));
  const saveEditDoc = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editForm.name, category: editForm.category,
        client_id: editForm.client_id === "none" ? null : editForm.client_id,
        company_id: editForm.company_id === "none" ? null : editForm.company_id,
        expiration_date: editForm.expiration_date || null,
        client_visible: editForm.client_visible,
      };
      const r = await api.patch(`/cc/documents/${editRow}`, payload);
      setRows((rows) => rows.map((x) => x.id === editRow ? { ...x, ...r.data } : x));
      toast.success("Document updated"); setEditRow(null); setEditForm(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="documents-page">
      <SectionHeader title="Documents" subtitle="Securely stored files for clients and companies."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-document-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={18} weight="bold" /> Upload</Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
              <DialogHeader><DialogTitle>Upload Document</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Securely store a file for a client or company.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div><Label>File *</Label><Input type="file" data-testid="document-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-zinc-900/60 border-white/10" /></div>
                <div><Label>Display name</Label><Input data-testid="document-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={file?.name || ""} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={(v) => set("category", v)}>
                      <SelectTrigger data-testid="document-category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Expiration date</Label><Input type="date" value={form.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} /></div>
                  <div>
                    <Label>Client</Label>
                    <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">None</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name || c.business_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Company</Label>
                    <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent><SelectItem value="none">None</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
                  <div><div className="text-sm font-medium">Visible to client</div><div className="text-xs text-muted-foreground">Private/admin only by default</div></div>
                  <Switch checked={form.client_visible} onCheckedChange={(v) => set("client_visible", v)} data-testid="document-visible-switch" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-document-button" disabled={saving} onClick={save} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Uploading…" : "Upload"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {loading ? <div className="text-muted-foreground">Loading…</div> :
        rows.length === 0 ? <EmptyState icon={FileText} title="No documents" hint="Upload IDs, contracts, or business documents." /> : (
          <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="documents-list">
            {rows.map((d) => (
              <div key={d.id} data-testid={`document-row-${d.id}`} className="flex items-center justify-between gap-4 px-4 py-3.5">
                <div className="min-w-0">
                  <div className="font-medium truncate">{d.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {d.category}{d.expiration_date ? ` · expires ${fmtDate(d.expiration_date)}` : ""} · {fmtDate(d.created_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {d.client_visible && <StatusBadge status="Active" />}
                  <Button size="icon" variant="ghost" onClick={() => openEditDoc(d)} data-testid={`edit-document-${d.id}`}><PencilSimple size={18} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => view(d)} data-testid={`view-document-${d.id}`}><DownloadSimple size={18} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(d)} data-testid={`delete-document-${d.id}`} className="text-red-300 hover:text-red-200"><Trash size={18} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={!!editRow} onOpenChange={(o) => { if (!o) { setEditRow(null); setEditForm(null); } }}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>Edit Document</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Update this document's details and visibility.</DialogDescription></DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div><Label>Display name</Label><Input data-testid="edit-document-name-input" value={editForm.name} onChange={(e) => setE("name", e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={editForm.category} onValueChange={(v) => setE("category", v)}>
                    <SelectTrigger data-testid="edit-document-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Expiration date</Label><Input type="date" value={editForm.expiration_date || ""} onChange={(e) => setE("expiration_date", e.target.value)} /></div>
                <div>
                  <Label>Client</Label>
                  <Select value={editForm.client_id} onValueChange={(v) => setE("client_id", v)}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name || c.business_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Company</Label>
                  <Select value={editForm.company_id} onValueChange={(v) => setE("company_id", v)}>
                    <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent><SelectItem value="none">None</SelectItem>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.legal_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-white/10 p-3">
                <div className="text-sm font-medium">Visible to client</div>
                <Switch checked={editForm.client_visible} onCheckedChange={(v) => setE("client_visible", v)} data-testid="edit-document-visible-switch" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditRow(null); setEditForm(null); }}>Cancel</Button>
            <Button data-testid="save-edit-document-button" disabled={saving} onClick={saveEditDoc} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
